import type { GetShippingConfigurationResponseBodyType } from "@intelligems/ig-types/functions/shipping-configuration/shipping-configuration";
import type {
  MarketShippingTransition,
  MarketShippingTransitionKind,
} from "@intelligems/ig-types/dtos/shopify-shipping";

/**
 * How often the configuration is re-read while a preparation is queued or
 * running. The websocket push lands as soon as the queue records an outcome;
 * polling covers a missed push and a page that opened mid-run.
 */
export const SHIPPING_TRANSITION_POLL_INTERVAL_MS = 5_000;

/**
 * The queue lane is still working on it: nothing may cancel or restart it.
 * A boolean, not a type predicate: a false answer must not narrow the record
 * away, since a finished one still has a status to render.
 */
export const isShippingTransitionActive = (
  transition: MarketShippingTransition | null | undefined
): boolean =>
  transition?.status === "queued" || transition?.status === "running";

/**
 * The transition of one kind, or null. The record outlives the model switch
 * it prepared — a succeeded upgrade is still on file once the store is on
 * Markets — so each surface reads only the kind it owns.
 */
export const shippingTransitionOf = (
  transition: MarketShippingTransition | null | undefined,
  kind: MarketShippingTransitionKind
): MarketShippingTransition | null =>
  transition?.kind === kind ? transition : null;

/** A finished run whose recovery still has to clear before Shopify is switched. */
export const isShippingTransitionBlocked = (
  transition: MarketShippingTransition
): boolean =>
  transition.status === "succeeded" &&
  (transition.unresolvedBackups.length > 0 || transition.conflicts.length > 0);

export const shippingTransitionRefetchInterval = (
  data: GetShippingConfigurationResponseBodyType | undefined
): number | false =>
  isShippingTransitionActive(data?.capabilities.transition)
    ? SHIPPING_TRANSITION_POLL_INTERVAL_MS
    : false;
